/**
 * EditConditionsDialog — quick-edit modal for an existing estimate's
 * Project Conditions. Hosts the same `ProjectConditionsForm` used by the
 * /estimator/new wizard so the user has one consistent surface for entry
 * and edits. On save, PUTs `/api/estimates/{id}/conditions`, calls the
 * provided `onSaved` callback so the surrounding workspace can re-fetch
 * the estimate and re-derive timeline / planner overlays.
 *
 * Visibility rules: the trigger should only be rendered when an estimate
 * has `has_conditions=true` — legacy estimates without conditions stay
 * untouched (they remain editable in the legacy planner mode).
 *
 * Live "before/after weeks" delta:
 *   On open, we fetch the full estimate so we have its components/modifiers
 *   in scope. We cache the current `results.projected_weeks` as the BEFORE
 *   value, then re-call `/api/calculate` (read-only) on each debounced
 *   conditions change to compute the AFTER. Same instant-feedback pattern
 *   as the Pricing Impact Preview on the Rate Card — but for timeline.
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from './ui/sheet';
import { Button } from './ui/button';
import { Loader2, Save, AlertCircle, ArrowRight, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';
import {
  ProjectConditionsForm, EMPTY_CONDITIONS,
} from './estimator/ProjectConditionsForm';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EditConditionsDialog({ open, onOpenChange, estimateId, initialConditions, availableComponentNames, highlightBreakId = null, onSaved, isMigration = false, entityContext = null, hasConditionsValue = true }) {
  const [conditions, setConditions] = useState(EMPTY_CONDITIONS);
  const [salesLead, setSalesLead] = useState(null);
  const [dealStage, setDealStage] = useState({ stage: null, lost: false, history: [] });
  const [csLead, setCsLead] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  // v378 — Entity & Region selection (single source of truth for the estimate)
  const [entId, setEntId] = useState(null);
  const [reg, setReg] = useState(null);
  const initialEntRef = useRef(null);

  // Live-preview state. `baselineWeeks` is the saved estimate's current
  // projected_weeks at the moment the dialog opens; `previewWeeks` is the
  // recomputed value reflecting the in-flight conditions edit; `previewing`
  // is true while a debounced server preview is in flight.
  const [baselineWeeks, setBaselineWeeks] = useState(null);
  const [previewWeeks, setPreviewWeeks] = useState(null);
  const [previewMeta, setPreviewMeta] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  // Component names available on this estimate — passed to the form so
  // the per-break "specific components" multi-select has real choices.
  const [availableComponents, setAvailableComponents] = useState([]);
  const debounceRef = useRef(null);

  // Reset/refetch when the dialog opens — guarantees we never show stale data
  // if the user edits, closes, and re-opens before the parent re-fetched.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setBaselineWeeks(null);
    setPreviewWeeks(null);
    if (entityContext) {
      setEntId(entityContext.entityId || null);
      setReg(entityContext.region || null);
      initialEntRef.current = entityContext.entityId || null;
    }
    // UNSAVED-estimate path: no API roundtrip — operate purely on the
    // initialConditions + availableComponentNames passed by the parent.
    if (!estimateId) {
      setConditions({ ...EMPTY_CONDITIONS, ...(initialConditions || {}) });
      setAvailableComponents(availableComponentNames || []);
      setLoading(false);
      return undefined;
    }
    (async () => {
      try {
        // Pull the conditions + cached projected_weeks from the saved estimate.
        // The /preview-conditions endpoint already knows the components &
        // modifiers, so we don't need to ship them here.
        const r = await axios.get(
          `${API_URL}/api/estimates/${estimateId}`,
          { withCredentials: true },
        );
        const est = r.data?.estimate || r.data;
        if (cancelled || !est) return;

        const pc = initialConditions || est.project_conditions || {};
        setConditions({ ...EMPTY_CONDITIONS, ...pc });
        setSalesLead(est.sales_lead || null);
        setCsLead(est.cs_lead || null);
        setDealStage({ stage: est.deal_stage || (est.workflow_status === 'won' ? 'closed_won' : null), lost: est.workflow_status === 'lost', history: est.deal_stage_history || [] });
        setAvailableComponents(
          (est.selected_components || [])
            .map((c) => c.component_name)
            .filter(Boolean),
        );
        const baseline = (est.results || {}).projected_weeks;
        if (typeof baseline === 'number') {
          setBaselineWeeks(baseline);
          setPreviewWeeks(baseline);
        }
      } catch (e) {
        if (!cancelled) toast.error('Could not load conditions');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, estimateId, initialConditions, availableComponentNames]);

  // Debounced live preview — calls the lightweight
  // POST /estimates/{id}/preview-conditions which reuses the saved
  // estimate's components/modifiers server-side and only returns
  // {projected_weeks, conditions_floor_applied, ...}. Much smaller
  // round-trip than re-sending the full /calculate payload, especially
  // for estimates with many components or integrations.
  useEffect(() => {
    if (!open || !estimateId) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setPreviewing(true);
      try {
        const res = await axios.post(
          `${API_URL}/api/estimates/${estimateId}/preview-conditions`,
          { project_conditions: conditions },
          { withCredentials: true },
        );
        const w = res.data?.projected_weeks;
        if (typeof w === 'number') setPreviewWeeks(w);
        setPreviewMeta(res.data || null);
      } catch (e) {
        // Silent — preview is non-critical; Save still works.
      } finally {
        setPreviewing(false);
      }
    }, 350);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [conditions, estimateId, open]);

  const handleSave = async () => {
    setSaving(true);
    const _selEnt = entityContext ? (entityContext.entities || []).find(e => e.id === entId) : null;
    const _entChanged = entityContext && entId !== initialEntRef.current;
    const _entCcy = _entChanged ? (_selEnt?.default_currency_code || null) : null;
    try {
      // No-id path: parent persists into local state via onSaved(conditions).
      if (!estimateId) {
        if (entityContext?.onApplied) entityContext.onApplied(entId, reg, _entCcy);
        if (onSaved) await onSaved(conditions);
        toast.success('Project Conditions updated — save the estimate to persist.');
        onOpenChange(false);
        return;
      }
      const body = { project_conditions: conditions, has_conditions: hasConditionsValue };
      if (entityContext) {
        body.contracting_entity_id = entId || '';
        body.region = reg || '';
        if (_entCcy) body.currency_code = _entCcy;
      }
      await axios.put(
        `${API_URL}/api/estimates/${estimateId}/conditions`,
        body,
        { withCredentials: true },
      );
      if (entityContext?.onApplied) entityContext.onApplied(entId, reg, _entCcy);
      toast.success('Project Conditions updated — estimate & planner will reflect changes');
      if (onSaved) await onSaved();
      onOpenChange(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Save failed';
      toast.error(`Save failed: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl overflow-y-auto"
        data-testid="edit-conditions-dialog"
      >
        <SheetHeader>
          <SheetTitle className="text-[#0F2040]">
            {isMigration ? 'Set Up Project Conditions' : 'Edit Project Conditions'}
          </SheetTitle>
          <SheetDescription>
            {isMigration
              ? 'Migrate this estimate to the modern Conditions Flow. Saving here unlocks the goal-duration floor, hypercare extension, and planner overlays.'
              : "Updates here flow through to the Estimate (effort floor) and the read-only Planner overlays."}
          </SheetDescription>
        </SheetHeader>

        <div className="my-5">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-12 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading conditions…
            </div>
          ) : (
            <>
              <div
                className={`flex items-start gap-2 px-3 py-2 mb-4 rounded-md text-[11px] border ${
                  isMigration
                    ? 'bg-cyan-50 border-cyan-200 text-cyan-800'
                    : 'bg-amber-50 border-amber-200 text-amber-800'
                }`}
                data-testid={isMigration ? 'conditions-migration-banner' : 'conditions-edit-banner'}
              >
                <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <p>
                  {isMigration
                    ? <>Once saved, this estimate will be flagged <strong>Conditions Flow = true</strong>. The Planner timeline will recompute using your goal duration and any phase breaks you define.</>
                    : <>Saving will trigger an automatic recalculation of this estimate's projected weeks (using the new goal-duration floor) and refresh the Planner overlays.</>}
                </p>
              </div>
              <ProjectConditionsForm
                value={conditions}
                onChange={setConditions}
                disabled={saving}
                availableComponents={availableComponents}
                highlightBreakId={highlightBreakId}
                estimateId={estimateId}
                salesLead={salesLead}
                onSalesLeadChange={estimateId ? ((lead) => { setSalesLead(lead); if (onSaved) onSaved(); }) : null}
                csLead={csLead}
                onCSLeadChange={estimateId ? ((lead) => { setCsLead(lead); if (onSaved) onSaved(); }) : null}
                dealStage={estimateId ? { ...dealStage, onChange: (r) => { setDealStage({ stage: r.deal_stage, lost: r.deal_stage_lost, history: r.history || [] }); if (onSaved) onSaved(); } } : null}
                entityContext={entityContext ? {
                  entities: entityContext.entities || [],
                  entityId: entId,
                  region: reg,
                  onChange: (eid, r) => {
                    if (eid !== entId) {
                      setEntId(eid);
                      const en = (entityContext.entities || []).find(x => x.id === eid);
                      const regions = en ? (en.regions?.length ? en.regions : (en.region ? [en.region] : [])) : [];
                      setReg(regions.length === 1 ? regions[0] : null);
                    } else {
                      setReg(r);
                    }
                  },
                } : null}
              />
              <DurationDeltaPreview
                baseline={baselineWeeks}
                preview={previewWeeks}
                previewing={previewing}
                meta={previewMeta}
              />
            </>
          )}
        </div>

        <SheetFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            data-testid="edit-conditions-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || loading}
            className="bg-[#0F2040] hover:bg-[#0F2040]/90 text-white"
            data-testid="edit-conditions-save-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-1" />
            )}
            {isMigration ? 'Migrate & Recalculate' : 'Save & Recalculate'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

/**
 * Inline before → after preview. Renders a sticky bar showing the cached
 * baseline weeks vs the recomputed preview, with a colored delta chip.
 *
 * - Green chip when weeks decrease (faster delivery)
 * - Amber chip when weeks increase (longer timeline) — the most common case
 *   when raising goal_duration_weeks
 * - Slate "≈ no change" when the delta is 0
 */
function DurationDeltaPreview({ baseline, preview, previewing, meta }) {
  if (typeof baseline !== 'number' || typeof preview !== 'number') return null;
  const delta = preview - baseline;
  const isZero = delta === 0;
  const tone = isZero
    ? 'bg-slate-50 text-slate-600 border-slate-200'
    : delta > 0
      ? 'bg-amber-50 text-amber-700 border-amber-200'
      : 'bg-emerald-50 text-emerald-700 border-emerald-200';
  const overrun = Math.max(0, parseInt(meta?.schedule_overrun_weeks || 0, 10) || 0);
  const anchored = !!meta?.explicit_go_live_anchor;
  const calendarSpan = meta?.calendar_span_weeks;
  const subtitle = anchored
    ? overrun > 0
      ? `go-live anchored · effort overruns by ${overrun}w`
      : `go-live anchored · ${calendarSpan ? `${calendarSpan}w calendar span` : 'fixed window'}`
    : 'live preview · effort-floor applied';
  return (
    <div
      className="mt-5 rounded-lg border border-[#4ABFCF]/30 bg-gradient-to-r from-[#4ABFCF]/5 to-[#0F2040]/5 p-4 sticky bottom-0"
      data-testid="conditions-duration-delta-preview"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2 text-xs text-slate-600">
          <CalendarDays className="w-3.5 h-3.5 text-[#4ABFCF]" />
          <span className="font-semibold text-[#0F2040]">Project Duration</span>
          <span className="text-slate-400">{subtitle}</span>
          {previewing && <Loader2 className="w-3 h-3 animate-spin text-slate-400 ml-1" />}
        </div>
        <div className="flex items-center gap-2 text-sm font-mono">
          <span className="text-slate-500" data-testid="duration-baseline">
            {baseline}w
          </span>
          <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
          <span className="text-[#0F2040] font-bold" data-testid="duration-preview">
            {preview}w
          </span>
          <span
            className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${tone}`}
            data-testid="duration-delta-chip"
          >
            {isZero ? '≈ no change' : `${delta > 0 ? '+' : ''}${delta}w`}
          </span>
        </div>
      </div>
      {anchored && overrun > 0 && (
        <div
          className="mt-3 flex items-start gap-2 text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-2.5 py-1.5"
          data-testid="conditions-overrun-warning"
        >
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>
            <strong>Schedule risk:</strong> committed go-live is anchored, but effort + breaks need
            {' '}{overrun} extra week{overrun === 1 ? '' : 's'} to fit. Consider compressing scope, dropping a break, or moving the go-live date.
          </span>
        </div>
      )}
    </div>
  );
}
